import { Body } from '@assembly-js/design-system';
import { t } from 'i18next';
import React, { useState } from 'react';

import { ModalWrapper } from 'src/legacy/components/Modals';
import { usePreviewStudioPlanChangeQuery } from 'src/services/api/studioCreditApi';

import { CreditTierSelect } from './CreditTierSelect';
import {
  PlanChangeModalButtons,
  PlanChangePrice,
  useStudioModalSheetProps,
} from './PlanChangeModalChrome';
import { SkeletonBar } from './PlansPageSkeleton';
import {
  formatDollars,
  getCreditSelectorOptions,
  StudioPlanSelection,
  toPlanChangePreviewRequest,
} from './studioPlanFeatures';

interface PlanChangeUpgradeModalProps {
  isOpen: boolean;
  selection: StudioPlanSelection;
  isLoading: boolean;
  onClose: () => void;
  /** Confirms the upgrade at the modal's selected TOTAL credits. */
  onConfirm: (totalCredits: number) => void;
}

export const PlanChangeUpgradeModal: React.FC<PlanChangeUpgradeModalProps> = ({
  isOpen,
  selection,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const sheetProps = useStudioModalSheetProps();
  const { tier, interval } = selection;
  const includedCredits = tier.includedCredits ?? tier.credits;
  // Inject the card's chosen total so the seeded selection always renders,
  // even if it's off the ladder.
  const creditOptions = getCreditSelectorOptions(includedCredits, tier.credits);
  const [totalCredits, setTotalCredits] = useState(tier.credits);

  // Per-argument caching makes flipping between credit options instant; the
  // skeleton below is the loading state while a new selection previews.
  const {
    data: preview,
    isFetching: isPreviewLoading,
    isError: isPreviewError,
  } = usePreviewStudioPlanChangeQuery(
    toPlanChangePreviewRequest(selection, totalCredits),
    { skip: !isOpen },
  );

  // The preview endpoint ships together with this change; until it's
  // deployed (or on any failure) fall back to the catalog base price with no
  // due-today claim.
  const fallbackPriceCents =
    interval === 'year' && tier.annualCents !== undefined
      ? tier.annualCents
      : tier.monthlyCents;
  const fallbackSuffix =
    interval === 'year' && tier.annualCents !== undefined
      ? t(($) => $.page_studio_plans.plan_change.per_year)
      : t(($) => $.page_studio_plans.plan_change.per_month);
  const showPreview = !isPreviewError && preview !== undefined;

  return (
    <ModalWrapper
      open={isOpen}
      onClose={onClose}
      hasBackdrop
      hideDividers
      denseContent
      closeButton
      {...sheetProps}
      title={t(($) => $.page_studio_plans.plan_change.change_plan_title)}
      hideActions
      isLoading={isLoading}
    >
      {/* 20px desktop rhythm, 16px on the sheet (Figma 7130:61938). */}
      <div className="flex flex-col gap-4 px-4 pb-4 sm:gap-5 sm:px-5 sm:pb-5">
        <div className="flex flex-col gap-5 rounded-lg border border-solid border-non-hover-border p-5">
          <div className="flex flex-col gap-4">
            <Body size="lg" className="font-medium text-black-heading">
              {t(($) => $.page_studio_plans.plan_change.upgrade_title, {
                plan: selection.planTitle,
              })}
            </Body>
            {isPreviewLoading ? (
              <SkeletonBar className="h-9 w-32" />
            ) : (
              <PlanChangePrice
                amount={formatDollars(
                  showPreview
                    ? preview.amountDueTodayCents
                    : fallbackPriceCents,
                )}
                suffix={
                  showPreview
                    ? t(($) => $.page_studio_plans.plan_change.due_today)
                    : fallbackSuffix
                }
              />
            )}
          </div>
          {/* Same totals dropdown as the plan cards, so the selection made
              there reads back unchanged on this confirmation step. */}
          <CreditTierSelect
            options={creditOptions}
            includedCredits={includedCredits}
            selectedCredits={totalCredits}
            onSelectCredits={setTotalCredits}
          />
          {interval === 'year' && (
            // Annual bills the whole year upfront, extras included, while
            // credits keep arriving monthly — the one thing about annual a
            // customer can't infer from the amount due.
            <Body size="xs" className="text-gray-small">
              {t(($) => $.page_studio_plans.plan_change.annual_billing_note)}
            </Body>
          )}
        </div>
        <PlanChangeModalButtons
          cancelLabel={t(($) => $.page_studio_plans.plan_change.cancel)}
          confirmLabel={t(($) => $.page_studio_plans.plan_change.upgrade_cta, {
            plan: selection.planTitle,
          })}
          isLoading={isLoading}
          onCancel={onClose}
          onConfirm={() => {
            onConfirm(totalCredits);
          }}
        />
      </div>
    </ModalWrapper>
  );
};
